import * as React from 'react'

import { observer } from 'mobx-react'

import MobileResourceEditDrawer from '~/client/src/mobile/components/MobileResource/MobileResourceEditDrawer'
import { Button } from '~/client/src/shared/components/Button/Button'
import { PanelSections } from '~/client/src/shared/components/PanelSections/PanelSections'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import useSensors from '~/client/src/shared/hooks/useSensors'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  isShown: boolean
  projectId: string
  onClose: () => void
}

const MobileStruxTagCreateDrawer: React.FC<IProps> = ({
  isShown,
  projectId,
  onClose,
}) => {
  const [infuseId, setInfuseId] = React.useState('')
  const [name, setName] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)

  const { save, schema } = useSensors({ projectId })
  const defaultTemplateId = schema.templates[0]?.id ?? null

  React.useEffect(() => {
    if (!isShown) {
      return
    }

    setInfuseId('')
    setName('')
    setError(null)
  }, [isShown])

  const handleClose = React.useCallback(() => {
    if (loading) {
      return
    }
    onClose()
  }, [loading, onClose])

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const normalizedInfuseId = infuseId.trim()
      const normalizedName = name.trim()

      if (!normalizedInfuseId) {
        setError(Localization.translator.infuseIdIsRequired)
        return
      }

      if (normalizedInfuseId.includes(' ')) {
        setError(Localization.translator.infuseIdMustNotContainSpaces)
        return
      }

      setError(null)

      try {
        setLoading(true)
        await save(null, {
          infuseId: normalizedInfuseId,
          name: normalizedName || normalizedInfuseId,
          _templateId: defaultTemplateId,
        })

        setLoading(false)
        onClose()
      } catch {
        setLoading(false)
        setError(Localization.translator.failedToRegisterStruxTag)
      }
    },
    [defaultTemplateId, infuseId, name, onClose, save],
  )

  return (
    <MobileResourceEditDrawer open={isShown} onClose={handleClose}>
      <form
        className="tw:flex tw:flex-1 tw:flex-col tw:min-h-0"
        onSubmit={handleSubmit}
      >
        <PanelSections.Header
          title={Localization.translator.registerStruxTag}
          onClose={handleClose}
          closeDisabled={loading}
        />

        <PanelSections.Content className="tw:px-4 tw:pb-4">
          <StruxhubInput
            id="mobile-struxtag-create-infuse-id"
            label={Localization.translator.infuseId}
            value={infuseId}
            onChange={event => setInfuseId(event.target.value)}
            isRequiredTextHidden={true}
            isRequired={true}
          />
          <div className="tw:mt-2.5">
            <StruxhubInput
              id="mobile-struxtag-create-name"
              label={Localization.translator.name}
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder={Localization.translator.optional}
            />
          </div>
          {error && (
            <div className="tw:text-body-sm tw:text-destructive tw:mt-2.5">
              {error}
            </div>
          )}
        </PanelSections.Content>

        <PanelSections.Footer>
          <Button
            type="submit"
            variant="default"
            size="lg"
            disabled={loading}
            className="tw:w-full"
          >
            {loading && (
              <span data-icon="inline-start" className="tw:inline-flex">
                <Spinner size="sm" variant="white" />
              </span>
            )}
            {loading
              ? Localization.translator.registering
              : Localization.translator.register}
          </Button>
        </PanelSections.Footer>
      </form>
    </MobileResourceEditDrawer>
  )
}

export default observer(MobileStruxTagCreateDrawer)
